import {
  ApprovalMemberRow,
  ApprovalProjectRow,
  ApprovalTableRow,
  ApprovalTimeEntry,
  ApprovalTimeOffEntry,
} from '../types/time-tracking-approvals';
import { isLoadMoreRow } from './approval-load-more-row.helper';

// =============================================================================
// Type Guards
// =============================================================================
// Centralized type guards to safely check row types.
// If schema changes, update these guards in ONE place.
// =============================================================================

/**
 * Type guard to check if data is an ApprovalTimeOffEntry (leaf level, informational).
 * Only time-off rows carry 'timeOffTypeName'.
 */
export const isApprovalTimeOffEntry = (data: unknown): data is ApprovalTimeOffEntry => {
  if (typeof data !== 'object' || data === null) return false;
  return 'timeOffTypeName' in (data as Record<string, unknown>);
};

/**
 * Type guard to check if data is an ApprovalTimeEntry (leaf level).
 * Time entries have 'date' and 'status' but no 'members' or 'member';
 * time-off rows are excluded (they have no 'trackedHours').
 */
export const isApprovalTimeEntry = (data: unknown): data is ApprovalTimeEntry => {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return (
    'date' in obj &&
    'status' in obj &&
    'trackedHours' in obj &&
    !('members' in obj) &&
    !('member' in obj)
  );
};

/**
 * Type guard to check if data is an ApprovalMemberRow (second level).
 * Member rows have 'member' object and 'timeEntries' array.
 */
export const isApprovalMemberRow = (data: unknown): data is ApprovalMemberRow => {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return 'member' in obj && 'timeEntries' in obj && Array.isArray(obj.timeEntries);
};

/**
 * Type guard to check if data is an ApprovalProjectRow (top level).
 * Project rows have 'project' object and 'members' array.
 */
export const isApprovalProjectRow = (data: unknown): data is ApprovalProjectRow => {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return 'project' in obj && 'members' in obj && Array.isArray(obj.members);
};

// =============================================================================
// Row ids
// =============================================================================
// Row ids are STRUCTURAL: a worklog entry carries denormalized project/member ids, so its
// ancestors' row ids are derivable from the entry alone. That's what lets selection logic
// answer "is this entry already covered by a selected project/member?" without walking the
// table's row tree (which memoized cells deliberately don't have access to).
// The builders below are the single source of truth — the row mappers and the selection
// store use them too, and `getSelectionLevelForRowId` parses the same shapes.
// =============================================================================

/** Row id of a project row (top level). */
export const getApprovalProjectRowId = (projectId: number): string => `project-${projectId}`;

/** Row id of a member row — project-scoped, since a member appears under many projects. */
export const getApprovalMemberRowId = (projectId: number, memberId: number): string =>
  `${getApprovalProjectRowId(projectId)}-member-${memberId}`;

/** Row id of a worklog time-entry row (leaf level). */
export const getApprovalEntryRowId = (entryId: number): string => `entry-${entryId}`;

/** Row ids of a worklog entry's ancestors, nearest first (member, then project). */
export const getApprovalEntryAncestorRowIds = (entry: ApprovalTimeEntry): string[] => [
  getApprovalMemberRowId(entry.projectId, entry.memberId),
  getApprovalProjectRowId(entry.projectId),
];

/**
 * Get row ID from approval table row data.
 * Centralized logic to generate unique IDs for each row type.
 */
export const getApprovalRowId = (row: ApprovalTableRow): string => {
  if (isLoadMoreRow(row)) {
    return row.id;
  }
  if (isApprovalTimeOffEntry(row)) {
    return row.id;
  }
  if (isApprovalTimeEntry(row)) {
    return getApprovalEntryRowId(row.id);
  }
  if (isApprovalMemberRow(row)) {
    return row.id;
  }
  // ApprovalProjectRow
  return (row as ApprovalProjectRow).id;
};

/**
 * Get sub-rows from approval table row data.
 * Returns members for project rows, timeEntries for member rows, undefined for time entries.
 */
export const getApprovalSubRows = (row: ApprovalTableRow): ApprovalTableRow[] | undefined => {
  if (isLoadMoreRow(row) || isApprovalTimeOffEntry(row)) {
    return undefined;
  }
  if (isApprovalProjectRow(row)) {
    return row.members;
  }
  if (isApprovalMemberRow(row)) {
    return row.timeEntries;
  }
  return undefined;
};

/**
 * Check if approval row has expandable children.
 */
export const hasApprovalChildren = (row: ApprovalTableRow): boolean => {
  if (isLoadMoreRow(row) || isApprovalTimeOffEntry(row)) {
    return false;
  }
  if (isApprovalProjectRow(row)) {
    return row.members.length > 0 || !!row.hasChildren;
  }
  if (isApprovalMemberRow(row)) {
    return row.timeEntries.length > 0 || !!row.hasChildren;
  }
  return false;
};
